import posthog from "posthog-js"
import { config } from "/app.config"

export const POSTHOG_KEY = process.env.VUE_APP_POSTHOG_PROJECT_TOKEN
export const POSTHOG_HOST = process.env.VUE_APP_POSTHOG_HOST || "https://us.i.posthog.com"

/** Must match zeno.fm PricingPlans / PostHog experiment key exactly. */
export const PRICING_EXPERIMENT_FLAG = "pricing-monthly-vs-yearly-trial"
const VARIANT_STORAGE_KEY = "ph_pricing_experiment_variant"

const BOOTSTRAP_COOKIE = "ph_bootstrap"

export const isPostHogEnabled = () => {
  return Boolean(POSTHOG_KEY && POSTHOG_KEY.length && config.isZeno)
}

const clearBootstrapCookie = () => {
  // Clear both host-only and shared .zeno.fm variants.
  document.cookie = `${BOOTSTRAP_COOKIE}=; path=/; max-age=0`
  document.cookie = `${BOOTSTRAP_COOKIE}=; domain=.zeno.fm; path=/; max-age=0`
}

const parseBootstrapPayload = (raw) => {
  if (!raw) {
    return null
  }

  try {
    // Accept both URI-encoded payloads and plain JSON (Nitro setCookie).
    let decoded = raw
    try {
      decoded = decodeURIComponent(raw)
    } catch (e) {
      // already decoded
    }

    const data = JSON.parse(decoded)
    const distinctID = data.distinctID || data.distinct_id
    const sessionID = data.sessionID || data.session_id

    if (!distinctID) {
      return null
    }

    return {
      distinctID,
      ...(sessionID ? { sessionID } : {}),
    }
  } catch (e) {
    return null
  }
}

const readBootstrapFromCookie = () => {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${BOOTSTRAP_COOKIE}=([^;]*)`))
    if (!match) {
      return null
    }

    const bootstrap = parseBootstrapPayload(match[1])
    clearBootstrapCookie()
    return bootstrap
  } catch (e) {
    return null
  }
}

const readBootstrapFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search)
    const distinctID = params.get("ph_distinct_id")
    const sessionID = params.get("ph_session_id")

    if (!distinctID) {
      return null
    }

    return {
      distinctID,
      ...(sessionID ? { sessionID } : {}),
    }
  } catch (e) {
    return null
  }
}

/**
 * Init PostHog once (Zeno brand only). Prefer URL bootstrap IDs from the marketing
 * site, then the cross-subdomain cookie handoff, so pricing-experiment persons stay linked.
 */
export const initPostHog = () => {
  if (!isPostHogEnabled()) {
    return null
  }

  if (window.__posthogInitialized) {
    return posthog
  }

  const bootstrapIds = readBootstrapFromUrl() || readBootstrapFromCookie() || {}
  const bootstrap = {}

  if (bootstrapIds.distinctID) {
    bootstrap.distinctID = bootstrapIds.distinctID
  }
  if (bootstrapIds.sessionID) {
    bootstrap.sessionID = bootstrapIds.sessionID
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    persistence: "localStorage+cookie",
    // Manual experiment/conversion events only — auto pageviews burned bill volume.
    capture_pageview: false,
    capture_pageleave: false,
    autocapture: false,
    ...(Object.keys(bootstrap).length ? { bootstrap } : {}),
  })

  // Cache experiment assignment while still on the bootstrapped (marketing) distinct_id,
  // before Keycloak identify() can change the id used for flag hashing.
  posthog.onFeatureFlags(() => {
    persistPricingExperimentVariant(posthog.getFeatureFlag(PRICING_EXPERIMENT_FLAG))
  })

  window.__posthogInitialized = true
  return posthog
}

export const identifyPostHogUser = (user) => {
  if (!isPostHogEnabled() || !user?.id) {
    return
  }

  try {
    posthog.identify(String(user.id), {
      email: user.email,
      first_name: user.basic_info?.first_name,
      last_name: user.basic_info?.last_name,
    })
  } catch (e) {
    console.log("[PostHog] identify error: ", e)
  }
}

export const trackPostHogEvent = (eventName, properties = {}) => {
  try {
    if (!isPostHogEnabled()) {
      return
    }

    posthog.capture(eventName, properties)
  } catch (e) {
    console.log("[PostHog] Error: ", e)
  }
}

/**
 * Pricing experiment variant for the current person.
 * Prefers a session-cached value from the first flag load (pre-identify) so checkout
 * stays aligned with the zeno.fm assignment.
 * @returns {'control'|'test'|null} null when flag is off / unknown (baseline UX).
 */
export const getPricingExperimentVariant = () => {
  if (!isPostHogEnabled()) {
    return null
  }

  try {
    const cached = sessionStorage.getItem(VARIANT_STORAGE_KEY)
    if (cached === "control" || cached === "test") {
      return cached
    }
  } catch (e) {
    // sessionStorage unavailable
  }

  try {
    const variant = posthog.getFeatureFlag(PRICING_EXPERIMENT_FLAG)
    persistPricingExperimentVariant(variant)
    if (variant === "control" || variant === "test") {
      return variant
    }
    return null
  } catch (e) {
    console.log("[PostHog] getFeatureFlag error: ", e)
    return null
  }
}

const persistPricingExperimentVariant = (variant) => {
  if (variant !== "control" && variant !== "test") {
    return
  }

  try {
    if (!sessionStorage.getItem(VARIANT_STORAGE_KEY)) {
      sessionStorage.setItem(VARIANT_STORAGE_KEY, variant)
    }
  } catch (e) {
    // sessionStorage unavailable
  }
}

/**
 * Call `callback(variant)` now and whenever flags reload.
 * @returns {Function} unsubscribe
 */
export const onPricingExperimentReady = (callback) => {
  if (!isPostHogEnabled()) {
    callback(null)
    return () => {}
  }

  const apply = () => {
    callback(getPricingExperimentVariant())
  }

  apply()
  return posthog.onFeatureFlags(apply)
}
